import { useEffect, useRef, useState } from "react";
import useFavorites from "@/hooks/useFavorites";

const shareTargets = [
  { key: "weibo", label: "微博", icon: "ri-weibo-fill" },
  { key: "wechat", label: "微信", icon: "ri-wechat-fill" },
  { key: "link", label: "复制链接", icon: "ri-link" },
];

export default function ShareFavorite({ id, title }: { id: string; title: string }) {
  const { isFavorite, toggle } = useFavorites();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState("");
  const timerRef = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const favorited = isFavorite(id);

  const showToast = (msg: string) => {
    setToast(msg);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setToast(""), 2200);
  };

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleShare = (key: string, label: string) => {
    if (key === "link") {
      const url = window.location.href;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(
          () => showToast("链接已复制，可粘贴分享（演示）"),
          () => showToast("复制失败，请手动复制地址栏链接")
        );
      } else {
        showToast("复制失败，请手动复制地址栏链接");
      }
    } else {
      showToast(`已模拟分享到${label}：${title}（演示）`);
    }
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-share-line"></i>
          分享
        </button>
        {open && (
          <div className="absolute right-0 top-12 z-30 w-44 p-2 rounded-lg bg-background-50 border border-background-200 shadow-none animate-fade-up">
            {shareTargets.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => handleShare(t.key, t.label)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className={`${t.icon} text-base`}></i>
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          toggle(id);
          showToast(favorited ? "已取消收藏（演示）" : "已加入收藏（演示）");
        }}
        className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-md border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
          favorited
            ? "bg-accent-500 border-accent-500 text-primary-950"
            : "border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
        }`}
      >
        <i className={favorited ? "ri-star-fill" : "ri-star-line"}></i>
        {favorited ? "已收藏" : "收藏"}
      </button>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-primary-900 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}